"use client"

import { useEffect, useMemo, useState, type ReactNode } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { ArrowRight, Loader2, Mic, Shield, Star, Tag, Users, Zap } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useUser } from "@/lib/hooks/use-user"
import { useLogger } from "@/lib/hooks/use-logger"
import { useToast } from "@/components/ui/use-toast"
import { fetchWithTrace } from "@/lib/fetch-with-trace"
import { cn } from "@/lib/utils"
import {
  PLANS,
  getPromoCouponForPlan,
  formatPlanPrice,
  getPlanDisplayPricing,
  getPlansByCategory,
  isPlanSoldOut,
  isTemporaryAccessPlan,
  type PlanName,
  type PricingCurrency,
  isPopularPlan,
} from "@/lib/config/plans"

import { TABS, TAB_STATS, PLAN_CARDS, type PricingTab } from "./pricing-plan-cards"

import { PricingPlanCard } from "./PricingPlanCard"
import { authPathWithNext, checkoutPath, pricingTabPath } from "@/lib/payments/checkout-navigation"
import { HALLOWEEN_COUPON_CODE, HALLOWEEN_OFFER_DISCLOSURE } from "@/lib/payments/halloween-offer"
import { useHalloweenPricing } from "@/lib/hooks/use-halloween-pricing"

interface SubscriptionPlansContentProps {
  pricingCurrency: PricingCurrency
  initialTab?: PricingTab
  questionPlan?: ReactNode
  /** Admin catalog toggle — false hides the weekly card from new buyers. */
  weeklyPlanEnabled?: boolean
}

export default function SubscriptionPlansContent({
  pricingCurrency,
  weeklyPlanEnabled = true,
  initialTab = "subscription",
  questionPlan,
}: SubscriptionPlansContentProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const requestedCoupon = searchParams.get("coupon")
  const campaignQuoteFor = useHalloweenPricing(requestedCoupon, pricingCurrency)
  const { user, subscription, isSubscribed, loading: userLoading } = useUser()
  const logger = useLogger()
  const { toast } = useToast()
  const [hasHydrated, setHasHydrated] = useState(false)
  useEffect(() => setHasHydrated(true), [])
  const [activeTab, setActiveTab] = useState<PricingTab>(initialTab)
  useEffect(() => setActiveTab(initialTab), [initialTab])
  const [activatingPlan, setActivatingPlan] = useState<PlanName | null>(null)

  const currentPlan = isSubscribed ? (subscription?.plan_name ?? null) : null

  useEffect(() => {
    if (!userLoading) {
      logger.info("subscription_plans_viewed", {
        event: "subscription_plans_viewed",
        current_plan: currentPlan,
        is_subscribed: isSubscribed,
        pricing_currency: pricingCurrency,
        active_tab: activeTab,
      })
    }
  }, [userLoading, currentPlan, isSubscribed, logger, pricingCurrency, activeTab])

  const formatPrice = (amount: number) => formatPlanPrice(amount, pricingCurrency)

  // Hide disabled weekly offers from new buyers, retaining a subscriber's current plan.
  const visiblePlans = useMemo(
    () =>
      (activeTab === "interview-questions" ? [] : getPlansByCategory(activeTab)).filter(
        (plan) => plan !== "weekly" || weeklyPlanEnabled || currentPlan?.toLowerCase() === "weekly"
      ),
    [activeTab, weeklyPlanEnabled, currentPlan]
  )

  // Credit packs remain repeatable purchases.
  const isCurrentPlan = (planId: string) =>
    PLANS[planId as PlanName]?.planType !== "credit-pack" &&
    !!isSubscribed &&
    !!currentPlan &&
    currentPlan.toLowerCase() === planId.toLowerCase()

  const handlePlanSelect = async (planId: PlanName) => {
    const cardConfig = PLAN_CARDS[planId as PlanName]
    logger.info("plan_selected", {
      event: "plan_selected",
      plan_id: planId,
      plan_name: cardConfig?.displayName,
      current_plan: currentPlan,
      tab: activeTab,
    })

    if (isPlanSoldOut(planId)) return

    if (!user) {
      router.push(
        authPathWithNext(
          "signin",
          requestedCoupon ? checkoutPath(planId, requestedCoupon) : "/pricing"
        )
      )
      return
    }

    // Existing subscriptions route to billing; credit packs can be purchased again.
    if (isCurrentPlan(planId)) {
      router.push("/settings?tab=billing")
      return
    }

    if (isTemporaryAccessPlan(planId)) {
      setActivatingPlan(planId)
      try {
        const { response } = await fetchWithTrace("/api/subscriptions/temporary-access", {
          logger,
          method: "POST",
        })
        const data = await response.json()
        if (!response.ok) {
          throw new Error(data.error || "Unable to activate temporary access")
        }
        if (data.alreadyActive) {
          toast({
            title: "Unlimited access is already active",
            description: "Your existing plan has not been changed.",
          })
          router.push("/settings?tab=billing")
          return
        }
        router.push(
          `/payment-success?grant=temporary&paymentId=${encodeURIComponent(data.paymentId ?? "")}`
        )
      } catch (error) {
        toast({
          title: "Activation failed",
          description: error instanceof Error ? error.message : "Please try again.",
          variant: "destructive",
        })
      } finally {
        setActivatingPlan(null)
      }
      return
    }

    router.push(checkoutPath(planId, requestedCoupon))
  }

  if (!hasHydrated || userLoading) {
    return (
      <div className="relative flex flex-col items-center justify-center overflow-hidden py-24">
        <div className="relative z-20 text-center space-y-4">
          <h2 className="text-xl mb-4 text-zinc-900 dark:text-zinc-100 font-medium">
            Loading pricing...
          </h2>
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto" />
        </div>
      </div>
    )
  }

  const stats = activeTab === "interview-questions" ? [] : (TAB_STATS[activeTab] ?? [])
  const gridCols =
    visiblePlans.length === 1
      ? "md:grid-cols-1 max-w-md mx-auto"
      : visiblePlans.length === 2
        ? "md:grid-cols-2 max-w-4xl mx-auto"
        : "md:grid-cols-3"

  return (
    <div className="relative">
      <div className="relative z-10">
        <div className="px-4">
          <div className="max-w-2xl mx-auto">
            <div
              role="tablist"
              aria-label="Pricing categories"
              className="flex items-center gap-1 rounded-full border border-zinc-200 bg-white/60 p-1 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-zinc-900/60"
            >
              {TABS.map((tab) => {
                const isActive = tab.id === activeTab
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => {
                      setActiveTab(tab.id)
                      router.replace(pricingTabPath(tab.id, searchParams.toString()), {
                        scroll: false,
                      })
                    }}
                    className={cn(
                      "flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-full px-2 py-2 text-xs sm:px-4 sm:text-sm font-semibold transition-all duration-200",
                      isActive
                        ? "bg-primary text-black shadow-md shadow-primary/20"
                        : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
                    )}
                  >
                    <span className="hidden sm:inline-flex">{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {activeTab === "interview-questions" ? (
          <div className="px-4 pb-12 pt-8" role="tabpanel" aria-label="Interview Questions">
            {questionPlan}
          </div>
        ) : (
          <>
            {/* Plan grid */}
            <div className="mx-auto max-w-6xl px-4 pb-8 pt-8">
              <div className={cn("grid gap-6", gridCols)}>
                {visiblePlans.map((planId) => {
                  const card = PLAN_CARDS[planId]
                  const plan = PLANS[planId]
                  const displayPricing = getPlanDisplayPricing(planId, pricingCurrency)
                  const campaignQuote = campaignQuoteFor(planId)
                  const price = campaignQuote?.totalPayable ?? displayPricing?.amount ?? plan.price
                  const originalPrice = displayPricing?.originalAmount
                  const annual = planId === "yearly"
                  const headlinePrice = annual ? price / 12 : price
                  const cardPromo = campaignQuote
                    ? { code: HALLOWEEN_COUPON_CODE, percentOff: 60 }
                    : getPromoCouponForPlan(planId)
                  const soldOut = isPlanSoldOut(planId)
                  const temporaryAccess = isTemporaryAccessPlan(planId)
                  const isActivating = activatingPlan === planId
                  const isPopular = isPopularPlan(planId)
                  const isFeatured = isPopular && !soldOut

                  return (
                    <PricingPlanCard
                      key={planId}
                      planId={planId}
                      headingId={`pricing-${planId}`}
                      title={card.displayName}
                      description={card.description}
                      icon={card.icon}
                      accent={card.accent}
                      featured={isFeatured}
                      badge={
                        soldOut ? (
                          <Badge className="rounded-full border-0 bg-zinc-700 px-3 py-1 text-xs font-bold text-white shadow-lg">
                            Sold Out
                          </Badge>
                        ) : isPopular ? (
                          <Badge className="rounded-full border-0 bg-gradient-to-r from-primary to-yellow-500 px-3 py-1 text-xs font-bold text-black shadow-lg">
                            <Star className="w-3.5 h-3.5 mr-1.5 fill-black" />
                            Most Popular
                          </Badge>
                        ) : null
                      }
                      status={
                        isCurrentPlan(planId) ? (
                          <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20 px-3 py-1 rounded-full text-xs font-semibold">
                            Current Plan
                          </Badge>
                        ) : null
                      }
                      pricing={
                        <>
                          {!temporaryAccess && originalPrice && originalPrice > price && (
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-base font-medium text-zinc-400 line-through dark:text-zinc-500">
                                {formatPrice(annual ? originalPrice / 12 : originalPrice)}
                              </span>
                              <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20 text-xs px-2 py-0.5 rounded-full">
                                {cardPromo.percentOff}% OFF
                              </Badge>
                            </div>
                          )}
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-bold text-zinc-900 dark:text-white">
                              {formatPrice(headlinePrice)}
                            </span>
                            <span className="text-zinc-500 dark:text-zinc-400 text-sm font-medium">
                              /{annual ? "month" : card.periodLabel}
                            </span>
                          </div>
                          {annual ? (
                            <p className="mt-2 text-sm font-medium text-zinc-600 dark:text-zinc-300">
                              Billed {formatPrice(price)} yearly
                            </p>
                          ) : null}
                          <div className="mt-2 text-sm font-semibold text-primary">
                            {card.callCreditsHeadline}
                          </div>
                          {temporaryAccess ? (
                            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-green-500/20 bg-green-500/10 px-2.5 py-1 text-xs font-semibold text-green-700 dark:text-green-400">
                              No payment required
                            </div>
                          ) : campaignQuote && !campaignQuote.appliedCoupon ? (
                            <p
                              role="status"
                              className="mt-2 text-sm text-amber-700 dark:text-amber-300"
                            >
                              {campaignQuote.couponError || "Checking HALLOWEEN60…"}
                            </p>
                          ) : (
                            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-green-500/20 bg-green-500/10 px-2.5 py-1 text-xs font-semibold text-green-700 dark:text-green-400">
                              <Tag className="w-3.5 h-3.5" />
                              Code <span className="font-bold">{cardPromo.code}</span> ·{" "}
                              {cardPromo.percentOff}% off applied
                            </div>
                          )}
                          {campaignQuote?.appliedCoupon && (
                            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
                              {HALLOWEEN_OFFER_DISCLOSURE}
                            </p>
                          )}
                        </>
                      }
                      featureHighlight={
                        card.includesQuestionLibrary ? (
                          <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 dark:border-purple-800 dark:bg-purple-950/40">
                            <div className="flex items-start gap-2">
                              <Badge className="shrink-0 border-0 bg-purple-600 px-2 py-0.5 text-xs font-bold text-white">
                                New
                              </Badge>
                              <strong className="text-[13px] font-bold leading-5 text-purple-900 dark:text-purple-100">
                                Access to latest anonymized interview questions
                              </strong>
                            </div>
                          </div>
                        ) : null
                      }
                      features={card.features}
                      actions={
                        <Button
                          onClick={() => handlePlanSelect(planId)}
                          disabled={
                            soldOut ||
                            isCurrentPlan(planId) ||
                            isActivating ||
                            Boolean(campaignQuote && !campaignQuote.canSubmit)
                          }
                          className={cn(
                            "w-full text-sm font-semibold transition-all duration-300",
                            soldOut || isCurrentPlan(planId)
                              ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-500 cursor-not-allowed border border-zinc-200 dark:border-zinc-700"
                              : isFeatured
                                ? "bg-primary hover:bg-primary/90 text-black shadow-lg shadow-primary/20 hover:shadow-primary/40 hover:scale-[1.02]"
                                : "bg-zinc-900 dark:bg-white text-white dark:text-black hover:bg-zinc-800 dark:hover:bg-zinc-200 hover:scale-[1.02]"
                          )}
                        >
                          {isActivating ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Activating...
                            </>
                          ) : isCurrentPlan(planId) ? (
                            "Active Plan"
                          ) : soldOut ? (
                            "Sold Out"
                          ) : (
                            card.ctaLabel
                          )}
                        </Button>
                      }
                    />
                  )
                })}
              </div>
            </div>

            {/* Purchase assurances come after the plans, once a customer has compared options. */}
            <div className="px-4 pb-8">
              <div className="max-w-2xl mx-auto">
                <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 rounded-full border border-zinc-200 bg-white/60 px-4 py-2.5 text-xs text-zinc-600 backdrop-blur-md dark:border-white/10 dark:bg-zinc-900/40 dark:text-zinc-400">
                  {stats.map((stat, i) => (
                    <div key={stat.label} className="flex items-center gap-2">
                      <span className="text-primary">{stat.icon}</span>
                      <span className="font-medium">{stat.label}</span>
                      {i < stats.length - 1 && (
                        <span className="hidden sm:inline text-zinc-300 dark:text-zinc-700">|</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Practice interviews are a separate purchase journey, linked after plan comparison. */}
            <div className="px-4 pb-14">
              <div className="max-w-4xl mx-auto overflow-hidden rounded-3xl border border-primary/25 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-md shadow-xl shadow-primary/5">
                <div className="grid gap-5 p-5 md:grid-cols-[1fr_auto] md:items-center md:p-6">
                  <div className="flex items-start gap-4">
                    <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <Mic className="h-5 w-5" />
                    </div>
                    <div>
                      <Badge className="mb-2 rounded-full border-primary/20 bg-primary/10 px-2.5 py-0.5 text-xs text-primary hover:bg-primary/10">
                        Practice Interview Pricing
                      </Badge>
                      <h2 className="text-xl font-bold tracking-tight text-zinc-900 dark:text-white md:text-2xl">
                        Want to practice before the real interview?
                      </h2>
                      <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                        Run realistic AI mock interviews or book a 1-1 session with a senior
                        engineer. Practice credits are sold separately and never expire.
                      </p>
                    </div>
                  </div>
                  <Button
                    asChild
                    className="px-5 text-sm font-semibold text-black shadow-lg shadow-primary/20"
                  >
                    <Link href="/mock-interview/pricing">
                      Explore practice plans
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="relative px-4 py-16">
              <div className="absolute inset-0 bg-white/50 dark:bg-black/50 backdrop-blur-sm -z-10" />
              <div className="max-w-6xl mx-auto">
                <div className="mb-10 text-center">
                  <h2 className="mb-4 text-2xl font-bold text-zinc-900 dark:text-white md:text-3xl">
                    Interview Support for Your Next Role
                  </h2>
                  <p className="mx-auto max-w-2xl text-base text-zinc-600 dark:text-zinc-400">
                    Tools for developers, analysts, and job seekers preparing for their next
                    interview.
                  </p>
                </div>

                <div className="grid gap-6 md:grid-cols-3">
                  {[
                    {
                      icon: <Zap className="w-6 h-6" />,
                      title: "Proven Success",
                      desc: "146K+ jobs secured by engineers using PhantomCodeAI.",
                      color: "text-blue-500",
                      bg: "bg-blue-500/10",
                    },
                    {
                      icon: <Users className="w-6 h-6" />,
                      title: "Expert Community",
                      desc: "Connect with experienced engineers and industry professionals.",
                      color: "text-green-500",
                      bg: "bg-green-500/10",
                    },
                    {
                      icon: <Shield className="w-6 h-6" />,
                      title: "Quality Guaranteed",
                      desc: "Curated content from real interviews at top tech companies.",
                      color: "text-purple-500",
                      bg: "bg-purple-500/10",
                    },
                  ].map((feature, i) => (
                    <div
                      key={i}
                      className="rounded-3xl border border-zinc-200 bg-white/50 p-5 text-center dark:border-white/10 dark:bg-white/5"
                    >
                      <div
                        className={cn(
                          "mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl",
                          feature.bg
                        )}
                      >
                        <div className={feature.color}>{feature.icon}</div>
                      </div>
                      <h3 className="mb-2 text-lg font-bold text-zinc-900 dark:text-white">
                        {feature.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                        {feature.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
